import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BUILDER_LINKS, BuilderStepId, LensId, ProtectionId, builderPrices, builderSteps } from '../data/builder'
import { variantIdOf } from '../data/productLinks'
import {
  BuildState,
  buildAttributes,
  buildCartUrl,
  buildLines,
  buildTotal,
  completeHint,
  initialBuild,
  missingLinks,
  variantIds,
} from '../utils/builderCart'
import { trackCustom, trackEvent } from '../utils/tracking'
import { requestOffer } from '../utils/offerBus'

const DOWNSELL_SEEN_KEY = 'gv_hard_case_downsell_seen'

function downsellSeen(): boolean {
  try {
    return window.sessionStorage.getItem(DOWNSELL_SEEN_KEY) === '1'
  } catch {
    return false
  }
}

function markDownsellSeen() {
  try {
    window.sessionStorage.setItem(DOWNSELL_SEEN_KEY, '1')
  } catch {
    /* storage unavailable: the downsell may show again, which is harmless */
  }
}

export function useBuilder() {
  const [state, setState] = useState<BuildState>(initialBuild)
  const [step, setStep] = useState<BuilderStepId>('lens')
  const [started, setStarted] = useState(false)
  const [downsellOpen, setDownsellOpen] = useState(false)
  const [notConnected, setNotConnected] = useState(false)
  const [routing, setRouting] = useState(false)
  const hintShown = useRef<string>('')
  const stateRef = useRef(state)
  stateRef.current = state

  const lines = useMemo(() => buildLines(state), [state])
  const total = useMemo(() => buildTotal(lines), [lines])
  const hint = useMemo(() => completeHint(state), [state])

  // Fire complete_offer_shown once per distinct hint level.
  useEffect(() => {
    if (hint.kind === 'none' || hintShown.current === hint.kind) return
    hintShown.current = hint.kind
    trackCustom('complete_offer_shown', { level: hint.kind, total })
  }, [hint, total])

  const touch = useCallback(() => {
    if (started) return
    setStarted(true)
    trackCustom('essential_started')
  }, [started])

  const update = (patch: Partial<BuildState>) => {
    touch()
    setNotConnected(false)
    setState((current) => ({ ...current, ...patch }))
  }

  const setLens = (lens: LensId) => {
    trackCustom(`primary_lens_${lens}` as const)
    update({ lens })
  }

  const setFullLens = (on: boolean) => {
    trackCustom(on ? 'full_lens_added' : 'full_lens_removed')
    update({ fullLens: on })
  }

  const setHardCase = (on: boolean) => {
    trackCustom(on ? 'hard_case_added' : 'hard_case_removed')
    update({ hardCase: on })
  }

  const setCareKit = (on: boolean) => {
    trackCustom(on ? 'care_kit_added' : 'care_kit_removed')
    update({ careKit: on })
  }

  const setRx = (next: number) => {
    const rx = Math.max(0, Math.min(1, next))
    if (rx > state.rx) trackCustom('rx_added', { configuration: state.complete ? 'complete' : 'essential' })
    trackCustom('rx_quantity', { quantity: rx })
    update({ rx })
  }

  const setProtection = (protection: ProtectionId) => {
    trackCustom(
      protection === 'oneYear'
        ? 'one_year_protection_selected'
        : protection === 'lifetime'
          ? 'lifetime_protection_selected'
          : 'basic_protection_selected',
      { configuration: state.complete ? 'complete' : 'essential' },
    )
    update({ protection })
  }

  /**
   * Explicit customer action only: goes straight to the Shopify cart with the Complete System,
   * in their chosen lens. Care Kit, RX and Lifetime carry over when their product is connected.
   */
  const upgradeToComplete = () => {
    trackCustom('complete_offer_accepted', { from_total: total, level: hint.kind })
    touch()
    const completeState: BuildState = {
      ...state,
      complete: true,
      fullLens: false,
      hardCase: false,
      protection: state.protection === 'lifetime' ? 'lifetime' : 'basic',
    }
    setState(completeState)
    const connectable = buildLines(completeState).filter((line) => missingLinks([line]).length === 0)
    if (connectable.length === 0 || connectable[0].source !== 'product') {
      // Complete System link missing: fall back to its card on the page.
      requestOffer('single')
      return
    }
    route({}, completeState, connectable)
  }

  const revertToEssential = () => {
    trackCustom('complete_offer_reverted')
    update({ complete: false, protection: state.protection === 'lifetime' ? 'lifetime' : 'basic' })
  }

  const advanceTimer = useRef<number>(0)
  useEffect(() => () => window.clearTimeout(advanceTimer.current), [])

  const stepAfter = (id: BuilderStepId): BuilderStepId => {
    const index = builderSteps.findIndex((item) => item.id === id)
    return builderSteps[index + 1]?.id ?? id
  }

  const goToStep = (id: BuilderStepId) => {
    touch()
    window.clearTimeout(advanceTimer.current)
    setStep(id)
  }

  const nextStep = () => goToStep(stepAfter(step))

  const prevStep = () => {
    const index = builderSteps.findIndex((item) => item.id === step)
    const prev = builderSteps[index - 1]
    if (prev) goToStep(prev.id)
  }

  /** Moves on after a short beat, so the customer sees the product change first. */
  const advanceFrom = (from: BuilderStepId, delay: number) => {
    window.clearTimeout(advanceTimer.current)
    advanceTimer.current = window.setTimeout(() => {
      setStep((current) => (current === from ? stepAfter(from) : current))
    }, delay)
  }

  /** Step 1: preview a lens on the X1. The customer confirms it themselves before moving on. */
  const chooseLens = (lens: LensId) => {
    window.clearTimeout(advanceTimer.current)
    setLens(lens)
  }

  /** Optional add-on steps: "Add" or "No thanks" both advance naturally. */
  const decide = (kind: 'lensSystem' | 'case' | 'care' | 'rx', add: boolean) => {
    if (kind === 'lensSystem' && add !== state.fullLens) setFullLens(add)
    if (kind === 'case' && add !== state.hardCase) setHardCase(add)
    if (kind === 'care' && add !== state.careKit) setCareKit(add)
    if (kind === 'rx' && add !== state.rx > 0) setRx(add ? 1 : 0)
    if (!add) touch()
    // Lens system: show "added" for a short beat, then move straight on to the next step.
    advanceFrom(kind, add ? (kind === 'lensSystem' ? 700 : 320) : 120)
  }

  const route = (
    extra: { hardCaseDownsell?: boolean } = {},
    stateOverride?: BuildState,
    linesOverride?: ReturnType<typeof buildLines>,
  ) => {
    const state = stateOverride ?? stateRef.current
    const finalLines = [...(linesOverride ?? buildLines(state))]
    if (extra.hardCaseDownsell) {
      finalLines.splice(1, 0, {
        label: 'Hard Protective Case (checkout offer)',
        price: builderPrices.hardCaseDownsell,
        quantity: 1,
        source: 'builder',
        key: 'hardCaseDownsell',
      })
    }
    const finalTotal = buildTotal(finalLines)
    const finalState: BuildState = extra.hardCaseDownsell ? { ...state, hardCase: true } : state
    const attributes = buildAttributes(finalState, finalTotal)
    const href = buildCartUrl(finalLines, attributes)
    const params = {
      value: finalTotal,
      currency: 'USD',
      configuration_type: state.complete ? 'complete' : 'essential',
      primary_lens: state.lens,
      add_ons: finalLines.slice(1).map((line) => line.label),
      rx_quantity: state.rx,
      x1_quantity: 1,
    }
    trackCustom('checkout_started', params)
    if (!href) {
      setNotConnected(true)
      trackCustom('builder_not_connected', { missing: missingLinks(finalLines) })
      return
    }
    setRouting(true)
    trackEvent('AddToCart', {
      ...params,
      content_name: state.complete ? 'GritVue X1 Complete System (built)' : 'GritVue X1 Essential (built)',
      content_ids: variantIds(finalLines),
      content_type: 'product',
      num_items: finalLines.reduce((sum, line) => sum + line.quantity, 0),
    })
    trackEvent('InitiateCheckout', { value: finalTotal, currency: 'USD', content_ids: variantIds(finalLines) })
    window.setTimeout(() => {
      window.location.href = href
    }, 350)
  }

  /** Single Essential without a case gets one hard-case offer per session, never automatically added. */
  const checkout = () => {
    touch()
    // Only offer the $9.95 case when its own Shopify product is connected (never charge $14.95 for it).
    const downsellReady = Boolean(variantIdOf(BUILDER_LINKS.hardCaseDownsell))
    const eligible = downsellReady && !state.complete && !state.hardCase && !downsellSeen()
    if (eligible) {
      markDownsellSeen()
      trackCustom('hard_case_downsell_shown', { total })
      setDownsellOpen(true)
      return
    }
    route()
  }

  const acceptDownsell = () => {
    trackCustom('hard_case_downsell_accepted', { total })
    setDownsellOpen(false)
    route({ hardCaseDownsell: true })
  }

  const declineDownsell = () => {
    trackCustom('hard_case_downsell_declined', { total })
    setDownsellOpen(false)
    route()
  }

  return {
    state,
    step,
    lines,
    total,
    hint,
    downsellOpen,
    notConnected,
    routing,
    setLens,
    setFullLens,
    setHardCase,
    setCareKit,
    setRx,
    setProtection,
    upgradeToComplete,
    revertToEssential,
    goToStep,
    nextStep,
    prevStep,
    chooseLens,
    decide,
    checkout,
    acceptDownsell,
    declineDownsell,
    closeDownsell: () => setDownsellOpen(false),
  }
}

export type BuilderApi = ReturnType<typeof useBuilder>
